/* eslint-disable react-hooks/set-state-in-effect */
import { faTimes, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Drawer, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { toQueryString } from "../../../utils/uri";

interface GenerateInspectionReportDrawerProps {
  open: boolean;
  onClose: () => void;
}

function getInitialMonth(): number {
  return new Date().getMonth();
}

function getInitialYear(): number {
  return new Date().getFullYear();
}

export function GenerateInspectionReportDrawer(props: GenerateInspectionReportDrawerProps) {
  const { open, onClose } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [month, setMonth] = useState<number>(getInitialMonth());
  const [year, setYear] = useState<number>(getInitialYear());

  useEffect(() => {
    if (!open) {
      return;
    }

    setMonth(getInitialMonth());
    setYear(getInitialYear());
  }, [open]);

  const months = useMemo(() => {
    return [
      { value: 0, label: "January" },
      { value: 1, label: "February" },
      { value: 2, label: "March" },
      { value: 3, label: "April" },
      { value: 4, label: "May" },
      { value: 5, label: "June" },
      { value: 6, label: "July" },
      { value: 7, label: "August" },
      { value: 8, label: "September" },
      { value: 9, label: "October" },
      { value: 10, label: "November" },
      { value: 11, label: "December" },
    ];
  }, []);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 2019 }, (_, index) => 2020 + index).reverse();
  }, []);

  const handleGenerateReport = () => {
    const query = toQueryString({
      month: month + 1, // Convert to 1-based month for API
      year,
    });

    const reportUrl = `${window.location.origin}/print/inspection-report?${query}`;
    window.open(reportUrl, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      PaperProps={{
        sx: {
          width: isSmallScreen ? "100%" : "400px",
          maxWidth: "100%",
          zIndex: 1550,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, height: "100%", overflow: "auto" }}>
        {/* Header with title and close button */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Daily Inspections Report
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              width: 44,
              height: 44,
            }}
          >
            <Box sx={{ fontSize: "1.25rem" }}>
              <FontAwesomeIcon icon={faXmark} />
            </Box>
          </IconButton>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
          Select the month and year to generate the report.
        </Typography>

        <FormControl fullWidth sx={{ flexShrink: 0 }} variant="filled">
          <InputLabel>Month</InputLabel>
          <Select variant="filled" value={month} label="Month" onChange={(event) => setMonth(Number(event.target.value))}>
            {months.map((monthOption) => (
              <MenuItem key={monthOption.value} value={monthOption.value}>
                {monthOption.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ flexShrink: 0 }} variant="filled">
          <InputLabel>Year</InputLabel>
          <Select variant="filled" value={year} label="Year" onChange={(event) => setYear(Number(event.target.value))}>
            {yearOptions.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1, minHeight: 8 }} />

        <Stack gap={2} sx={{ flexShrink: 0 }}>
          <Button variant="contained" onClick={handleGenerateReport} sx={{ height: 50, width: "100%" }}>
            Generate Report
          </Button>
          <Button variant="outlined" onClick={onClose} sx={{ height: 50, width: "100%" }} startIcon={<FontAwesomeIcon icon={faTimes} />}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
